"use client";

import React, { useEffect, useState } from "react";
import Link from "next/link";

const COOKIE_KEY = "imperiumCookieConsent";

type Preferences = {
  functional: boolean;
};

export default function CookieConsent() {
  const [isOpen, setIsOpen] = useState(false);
  const [functional, setFunctional] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_KEY);
    if (!consent) {
      setIsOpen(true);
    }
  }, []);

  const save = (prefs: Preferences) => {
    localStorage.setItem(COOKIE_KEY, JSON.stringify({ essential: true, ...prefs, savedAt: Date.now() }));
    setIsOpen(false);
  };

  const acceptAll = () => save({ functional: true });
  const savePrefs = () => save({ functional });
  const rejectOptional = () => save({ functional: false });

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.45)",
          zIndex: 9998,
        }}
      />

      {/* Modal */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-modal-title"
        style={{
          position: "fixed",
          bottom: "50%",
          left: "50%",
          transform: "translate(-50%, 50%)",
          zIndex: 9999,
          background: "#fff",
          borderRadius: "12px",
          boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
          width: "min(520px, 94vw)",
          maxHeight: "90vh",
          overflowY: "auto",
          padding: "32px 28px 24px",
          display: "flex",
          flexDirection: "column",
          gap: "20px",
        }}
      >
        {/* Header */}
        <div>
          <h2 id="cookie-modal-title" style={{ fontSize: "1.2rem", fontWeight: 700, marginBottom: "10px" }}>
            Cookie Preferences
          </h2>
          <p style={{ fontSize: "0.875rem", color: "#444", lineHeight: 1.6 }}>
            We use cookies and similar technologies to improve your browsing experience, personalize content,
            analyze site traffic, and enhance platform performance.
          </p>
          <p style={{ fontSize: "0.875rem", color: "#444", lineHeight: 1.6, marginTop: "10px" }}>
            You can choose which cookies you would like to allow below. Essential cookies are required for the
            platform to function properly and cannot be disabled.
          </p>
        </div>

        {/* Cookie options */}
        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          {/* Essential */}
          <div
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              padding: "14px 16px",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
              <span style={{ fontWeight: 600, fontSize: "0.9rem" }}>Essential Cookies</span>
              <span
                style={{
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  color: "var(--chateau-green, #2e7d32)",
                  background: "#e8f5e9",
                  borderRadius: "20px",
                  padding: "2px 10px",
                }}
              >
                ✓ Always Active
              </span>
            </div>
            <p style={{ fontSize: "0.82rem", color: "#555", margin: 0 }}>
              Required for login, security, payments, and core platform functionality.
            </p>
          </div>

          {/* Functional */}
          <div
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              padding: "14px 16px",
            }}
          >
            <label
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px", cursor: "pointer" }}
            >
              <span style={{ fontWeight: 600, fontSize: "0.9rem" }}>Functional Cookies</span>
              <input
                type="checkbox"
                checked={functional}
                onChange={(e) => setFunctional(e.target.checked)}
                style={{ width: "18px", height: "18px", accentColor: "var(--chateau-green, #2e7d32)", cursor: "pointer" }}
              />
            </label>
            <p style={{ fontSize: "0.82rem", color: "#555", margin: 0 }}>
              Enables enhanced functionality and personalized features.
            </p>
          </div>
        </div>

        {/* Buttons */}
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <button
            onClick={acceptAll}
            className="btn btn-green"
            style={{ width: "100%", justifyContent: "center" }}
          >
            Accept All
          </button>
          <button
            onClick={savePrefs}
            className="btn btn-black"
            style={{ width: "100%", justifyContent: "center" }}
          >
            Save Preferences
          </button>
          <button
            onClick={rejectOptional}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "0.85rem",
              color: "#666",
              textDecoration: "underline",
              padding: "4px 0",
            }}
          >
            Reject Optional Cookies
          </button>
        </div>

        {/* Footer */}
        <p style={{ fontSize: "0.78rem", color: "#888", lineHeight: 1.5, margin: 0, borderTop: "1px solid #f0f0f0", paddingTop: "14px" }}>
          By clicking &quot;Accept All&quot; or &quot;Save Preferences,&quot; you agree to Imperium&apos;s use of cookies
          in accordance with our{" "}
          <Link href="/privacy-policy" style={{ color: "var(--chateau-green, #2e7d32)", fontWeight: 600 }}>
            Privacy Policy
          </Link>
          .
        </p>
      </div>
    </>
  );
}
